import { SIDENAV_COLLAPSED_WIDTH, SIDENAV_EXPANDED_WIDTH } from "@repo/ui";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/**
 * Viewport state published to MFEs.
 *
 * Delivered two ways so both MF consumers and single-spa consumers work:
 *   1. React context (useViewport()) — for MFEs that import ./ViewportContext
 *      via Module Federation from this host.
 *   2. window.__SAVIYNT_VIEWPORT__ snapshot + "saviynt:viewport:change"
 *      CustomEvent — for cross-repo / single-spa MFEs (e.g. ispm-ui) that
 *      can't import from this repo.
 */
export interface HostViewport {
  isMenuPinned: boolean;
  isMenuExpanded: boolean;
  sidenavWidth: number;
  headerHeight: number;
  viewportWidth: number;
  viewportHeight: number;
}

interface ViewportContextValue extends HostViewport {
  setMenuPinned: (pinned: boolean) => void;
  setMenuExpanded: (expanded: boolean) => void;
  headerRef: (node: HTMLElement | null) => void;
}

export const VIEWPORT_EVENT = "saviynt:viewport:change";
export const VIEWPORT_GLOBAL_KEY = "__SAVIYNT_VIEWPORT__";

declare global {
  interface Window {
    __SAVIYNT_VIEWPORT__?: HostViewport;
  }
}

const ViewportContext = createContext<ViewportContextValue | undefined>(
  undefined,
);

function getWindowSize() {
  if (typeof window === "undefined") {
    return { width: 0, height: 0 };
  }
  return { width: window.innerWidth, height: window.innerHeight };
}

export function ViewportProvider({ children }: { children: ReactNode }) {
  const [isMenuPinned, setIsMenuPinned] = useState(true);
  const [isMenuExpanded, setIsMenuExpanded] = useState(true);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [windowSize, setWindowSize] = useState(getWindowSize);

  const headerElRef = useRef<HTMLElement | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  // Measure header via ResizeObserver — no magic-number CSS.
  const headerRef = useCallback((node: HTMLElement | null) => {
    resizeObserverRef.current?.disconnect();
    resizeObserverRef.current = null;
    headerElRef.current = node;
    if (!node) return;

    setHeaderHeight(node.getBoundingClientRect().height);
    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (entry) setHeaderHeight(entry.contentRect.height);
      });
      observer.observe(node);
      resizeObserverRef.current = observer;
    }
  }, []);

  useEffect(
    () => () => {
      resizeObserverRef.current?.disconnect();
    },
    [],
  );

  // Track window resize.
  useEffect(() => {
    const onResize = () => setWindowSize(getWindowSize());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const sidenavWidth =
    isMenuPinned || isMenuExpanded
      ? SIDENAV_EXPANDED_WIDTH
      : SIDENAV_COLLAPSED_WIDTH;

  const viewport = useMemo<HostViewport>(
    () => ({
      isMenuPinned,
      isMenuExpanded,
      sidenavWidth,
      headerHeight,
      viewportWidth: Math.max(0, windowSize.width - sidenavWidth),
      viewportHeight: Math.max(0, windowSize.height - headerHeight),
    }),
    [
      isMenuPinned,
      isMenuExpanded,
      sidenavWidth,
      headerHeight,
      windowSize.width,
      windowSize.height,
    ],
  );

  // Bridge: publish to window + emit event for cross-repo/single-spa MFEs.
  useEffect(() => {
    if (typeof window === "undefined") return;
    window[VIEWPORT_GLOBAL_KEY] = viewport;
    window.dispatchEvent(new CustomEvent(VIEWPORT_EVENT, { detail: viewport }));
  }, [viewport]);

  useEffect(
    () => () => {
      if (typeof window !== "undefined") {
        delete window[VIEWPORT_GLOBAL_KEY];
      }
    },
    [],
  );

  const value = useMemo<ViewportContextValue>(
    () => ({
      ...viewport,
      setMenuPinned: setIsMenuPinned,
      setMenuExpanded: setIsMenuExpanded,
      headerRef,
    }),
    [viewport, headerRef],
  );

  return (
    <ViewportContext.Provider value={value}>
      {children}
    </ViewportContext.Provider>
  );
}

export function useViewport(): ViewportContextValue {
  const ctx = useContext(ViewportContext);
  if (!ctx) {
    throw new Error("useViewport must be used within a ViewportProvider");
  }
  return ctx;
}
